'use client'

import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@/store/hooks'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import {
    getTriagesByCompany,
    Triage,
    URGENCY_LABELS,
    URGENCY_COLORS,
} from '@/lib/api/triages/triage-api'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdSearch,
    MdClose,
    MdArrowForward,
    MdAdd,
    MdLocalHospital,
    MdPerson,
    MdAccessTime,
    MdFileDownload,
} from 'react-icons/md'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { ShareableModule } from '@/lib/api/share/share-api'

export default function TriagesPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('bookings', 'triage')
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showImportModal, setShowImportModal] = useState(false)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    const filters = useMemo(
        () => ({ search: debouncedSearch.trim() || undefined }),
        [debouncedSearch],
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getTriagesByCompany(user!.companyId!, params)
        },
        [user?.companyId],
    )

    const {
        items: displayedTriages,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Triage, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleClearSearch = () => {
        setSearchTerm('')
    }

    if (loading && displayedTriages.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando triagens...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                        <MdLocalHospital className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Triagem
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total}{' '}
                            {total === 1 ? 'registro' : 'registros'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar triagens..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    handleClearSearch()
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <div className="flex items-center gap-2">
                                    <button
                                        className="btn btn-outline btn-secondary gap-2"
                                        onClick={() => setShowImportModal(true)}
                                    >
                                        <MdFileDownload className="text-xl" />
                                        <span className="hidden sm:inline">Importar</span>
                                    </button>
                                    <button
                                        onClick={() => navigate('/bookings/triage/new')}
                                        className="btn btn-secondary gap-2"
                                    >
                                        <MdAdd className="text-lg" />
                                        <span className="hidden sm:inline">
                                            Nova Triagem
                                        </span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {displayedTriages.length === 0 && !loading ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdLocalHospital className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma triagem encontrada
                    </h3>
                    <p className="text-base-content/60 mb-6">
                        Comece adicionando sua primeira triagem
                    </p>
                    {canCreate && (
                        <button
                            onClick={() => navigate('/bookings/triage/new')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Nova Triagem
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {displayedTriages.map(triage => (
                            <div
                                key={triage.id}
                                onClick={() =>
                                    navigate(`/bookings/triage/${triage.id}`)
                                }
                                className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span
                                                className={`w-3 h-3 rounded-full ${
                                                    URGENCY_COLORS[
                                                        triage.urgency
                                                    ]
                                                }`}
                                            ></span>
                                            <span className="text-sm font-semibold">
                                                {URGENCY_LABELS[triage.urgency]}
                                            </span>
                                        </div>
                                        {triage.client && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/70 mb-1">
                                                <MdPerson className="text-base" />
                                                <span className="truncate">
                                                    {triage.client.name}
                                                </span>
                                            </div>
                                        )}
                                        {triage.chiefComplaint && (
                                            <p className="text-sm text-base-content/60 line-clamp-2">
                                                {triage.chiefComplaint}
                                            </p>
                                        )}
                                    </div>
                                    <MdArrowForward className="text-base-content/40 flex-shrink-0 ml-2" />
                                </div>

                                <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                    <div className="flex items-center gap-2 text-xs text-base-content/60">
                                        <MdAccessTime className="text-sm" />
                                        <span>
                                            {format(
                                                new Date(triage.triageAt),
                                                "dd/MM/yyyy 'as' HH:mm",
                                                { locale: ptBR }
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="triagem"
                        />
                    )}
                </>
            )}
            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.TRIAGE}
                moduleLabel="Triagens"
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response = await getTriagesByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: Triage) => (
                    <div>
                        <div className="flex items-center gap-2">
                            <span
                                className={`w-2.5 h-2.5 rounded-full ${URGENCY_COLORS[item.urgency]}`}
                            ></span>
                            <p className="text-sm font-medium truncate">
                                {URGENCY_LABELS[item.urgency]} - {item.client?.name || `Triagem #${item.id}`}
                            </p>
                        </div>
                        {item.chiefComplaint && (
                            <p className="text-xs text-base-content/60 truncate mt-0.5">
                                {item.chiefComplaint}
                            </p>
                        )}
                    </div>
                )}
                getItemId={(item: Triage) => item.id}
                getItemName={(item: Triage) =>
                    `${URGENCY_LABELS[item.urgency]} - ${item.client?.name || `Triagem #${item.id}`}`
                }
                onImportComplete={reload}
            />
        </div>
    )
}
